import type { ReactNode } from 'react';

export type WidgetStripe = 'pipeline' | 'queue' | 'sla' | 'tasks' | 'compliance' | 'approvals' | 'staff' | 'overrides';

const stripeColor: Record<WidgetStripe, string> = {
  pipeline: 'var(--pcb-blue)',
  queue: 'var(--pcb-green)',
  sla: 'var(--pcb-red)',
  tasks: 'var(--pcb-blue)',
  compliance: 'var(--pcb-amber-text)',
  approvals: 'var(--ent-application)',
  staff: 'var(--pcb-green)',
  overrides: 'var(--pcb-amber-text)',
};

export interface WidgetCardProps {
  title: string;
  count?: number | string;
  stripe?: WidgetStripe;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}

/** Primitive 13: Widget card (RPM 3×3 grid). */
export function WidgetCard({
  title,
  count,
  stripe = 'pipeline',
  actions,
  footer,
  children,
}: WidgetCardProps): JSX.Element {
  return (
    <div className="widget" style={{ borderLeftColor: stripeColor[stripe] }}>
      <header className="widget__head">
        <span className="widget__title">{title}</span>
        {count !== undefined && (
          <span className="pill pill-info" style={{ marginLeft: 'var(--sp-3)' }}>
            {count}
          </span>
        )}
        {actions && <div style={{ marginLeft: 'auto' }}>{actions}</div>}
      </header>
      <div className="widget__body">{children}</div>
      {footer && (
        <footer
          style={{
            background: 'var(--ink-50)',
            padding: 'var(--sp-4) var(--sp-7)',
            fontSize: 11,
            color: 'var(--ink-600)',
            borderTop: '1px solid var(--surface-border)',
          }}
        >
          {footer}
        </footer>
      )}
    </div>
  );
}

export interface WidgetRowProps {
  label: string;
  value: ReactNode;
  tone?: 'default' | 'red' | 'amber' | 'green';
  miniBar?: number; // 0..100
}

export function WidgetRow({ label, value, tone = 'default', miniBar }: WidgetRowProps): JSX.Element {
  const valueColor =
    tone === 'red' ? 'var(--pcb-red)' :
    tone === 'amber' ? 'var(--pcb-amber-text)' :
    tone === 'green' ? 'var(--pcb-green)' : 'var(--ink-900)';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-5)',
        padding: 'var(--sp-4) 0',
        borderBottom: '1px solid var(--ink-100)',
        fontSize: 12,
      }}
    >
      <span style={{ flex: 1, color: 'var(--ink-700)' }}>{label}</span>
      {miniBar !== undefined && (
        <span
          aria-hidden
          style={{
            width: 60,
            height: 6,
            background: 'var(--ink-200)',
            borderRadius: 'var(--r-full)',
            overflow: 'hidden',
          }}
        >
          <span
            style={{
              display: 'block',
              width: `${Math.max(0, Math.min(100, miniBar))}%`,
              height: '100%',
              background: valueColor,
            }}
          />
        </span>
      )}
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: valueColor }}>{value}</span>
    </div>
  );
}
